import { Dialog, dialogClasses } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { Role_Assignment, Company, Maybe, Additional_Address, Delivery_Hours } from '@kheops/graphql';
import { useCompanyByIdQuery } from '../../../queries/__generated__/companyById.generated';
import { DisplayPhoto } from '../../../common/common';
import { CompanyAddress } from '../../companyInfo/CompanyInfoCard';
import { Contact } from '../../CompanyContactList';
import BuyerInfoDeliveryView from './BuyerInfoDeliveryView';
import BuyerInfoGeneralView from './BuyerInfoGeneralView';
import BuyerInfoContactsView from './BuyerInfoContactsView';
import BuyerInfoInvoicingView from './BuyerInfoInvoicingView';
import BuyerInfoInvoicingAddressView from './BuyerInfoInvoicingAddressView';
import BuyerInfoInvoicingContactsView from './BuyerInfoInvoicingContactsView';
import BuyerInfoInvoicingHowToView from './BuyerInfoInvoicingHowToView';

type ContactInfo = Pick<Role_Assignment, 'is_delivery_contact' | 'is_invoicing_contact'> & {
  user: Contact;
};

export interface BuyerInfoViewProps {
  company: Pick<Company, 'id' | 'contactPhoneNumber' | 'tradeName' | 'legalName' | 'brand' | 'delivery_email' | 'delivery_instructions' | 'delivery_phone_number' | 'delivery_description' | 'invoicing_email' | 'invoicing_instructions' | 'invoicing_phone_number' | 'national_company_registration_number' | 'invoice_prefix'> & {
    addresses: CompanyAddress[];
    photos?: Maybe<DisplayPhoto[]>;
    role_assignments: ContactInfo[];
    delivery_address?: Maybe<Additional_Address>;
    delivery_hours?: Maybe<Pick<Delivery_Hours, 'opening_hour' | 'closing_hour' | 'week_day'>[]>;
    invoicing_address?: Maybe<Additional_Address>;
  };
  close: () => void;
  changeView: (view: BuyerView) => void;
  hideBackArrow?: boolean;
}

export type BuyerView = 'GENERAL' | 'DELIVERY' | 'INVOICING' | 'CONTACTS' | 'INVOICING_ADDRESS' | 'INVOICING_CONTACT' | 'INVOICING_HOW_TO';

export interface BuyerInfoDialogProps {
  companyId: string;
  close: () => void;
  isOpen: boolean;
  openView?: BuyerView;
}

const views = {
  GENERAL: (props: BuyerInfoViewProps) => <BuyerInfoGeneralView {...props} />,
  DELIVERY: (props: BuyerInfoViewProps) => <BuyerInfoDeliveryView {...props} />,
  INVOICING: (props: BuyerInfoViewProps) => <BuyerInfoInvoicingView {...props} />,
  INVOICING_ADDRESS: (props: BuyerInfoViewProps) => <BuyerInfoInvoicingAddressView {...props} />,
  INVOICING_CONTACT: (props: BuyerInfoViewProps) => <BuyerInfoInvoicingContactsView {...props} />,
  INVOICING_HOW_TO: (props: BuyerInfoViewProps) => <BuyerInfoInvoicingHowToView {...props} />,
  CONTACTS: (props: BuyerInfoViewProps) => <BuyerInfoContactsView {...props} />,
};

export default function BuyerInfoDialog({ companyId, close, isOpen, openView }: BuyerInfoDialogProps): React.JSX.Element {
  const [viewName, setViewName] = useState<BuyerView | void>();
  const { data } = useCompanyByIdQuery({
    variables: {
      companyId,
    },
    skip: !companyId,
  });

  const company = data?.company_by_pk;

  const view = useMemo(() => {
    return views[viewName || openView || 'GENERAL'];
  }, [openView, viewName]);

  const handleClose = useCallback(() => {
    close();
    setViewName(undefined);
  }, []);

  if (!company) {
    return <></>;
  }

  return (
    <Dialog
      onClose={handleClose}
      open={isOpen}
      slotProps={{
        paper: {
          sx: {
            m: {
              xs: 1,
              sm: 0,
            },
            height: 548,
            maxWidth: 500,
          },
        },
      }}
      sx={{
        [`& .${dialogClasses.container}`]: {
          alignItems: {
            xs: 'flex-end',
            sm: 'center',
          },
        },
      }}
    >
      {
        view({ company, close: handleClose, changeView: setViewName, hideBackArrow: !!openView })
      }
    </Dialog>
  );
}
